import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilesTobeSavedProps } from 'shared/interfaces/IModalForm';
import { IOutput } from 'shared/interfaces/IProfile';
import { IUpload } from 'shared/interfaces/IUpload';
import { arrayMove } from '@dnd-kit/sortable';
import { DragEndEvent } from '@dnd-kit/core';
import { useNotificationModal } from '@room-match/shared-ui-components';
import { getRemainingSpace } from 'shared/utils/getRemainingSpace';
type UseUploadImageProps = {
  current_saved_images?: IUpload[];
};

const NOTIFICATION_MESSAGE = {
  error: {
    title: 'Maximum Upload has been reached',
    subtitle: 'You cannot add more than 5 image',
  },
  warning: (length: number) => ({
    title: 'Maximum upload has been reached',
    subtitle:
      length < 5
        ? `Only ${length} image ${length > 1 ? 'slots' : 'slot'} remaining`
        : 'Maximum of 5 images can be uploaded',
  }),
};

export const useUploadImage = ({ current_saved_images }: UseUploadImageProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [notificationType, setNotificationType] = useState<'warning' | 'error'>('error');
  const [filesToBeSaved, setFilesToBeSaved] = useState<FilesTobeSavedProps[]>(
    current_saved_images ? [...current_saved_images] : [],
  );
  const { NotificationModal, isOpen, handleSetMessage, handleOpen } = useNotificationModal({
    type: 'error',
  });

  const MemoizeNotificationModal = useMemo(() => {
    return NotificationModal;
  }, [isOpen]);

  // Remove uploaded image
  const handleRemoveImages = useCallback(
    (index: number) => {
      const modifiedFilesToBeSaved = filesToBeSaved?.filter((_, uploadedIndex: number) => index !== uploadedIndex);
      setFilesToBeSaved(modifiedFilesToBeSaved);
    },

    [filesToBeSaved?.length],
  );

  // When dropping or selecting an image in file
  const handleOnDropImage = useCallback(
    async (dataFiles: File[]) => {
      if (filesToBeSaved?.length >= 5) {
        setNotificationType('error');
        handleOpen();
        return;
      }
      const remainingSpace = getRemainingSpace(filesToBeSaved?.length || 0);
      if (remainingSpace > 0 && dataFiles?.length > remainingSpace) {
        setNotificationType('warning');
        handleOpen();
      }
      setUploadedFiles(dataFiles);
    },

    [filesToBeSaved?.length],
  );

  // Files to be saved in database
  const handleFilesToBeSaved = useCallback((newFile: IOutput) => {
    setFilesToBeSaved((prev) => [...prev, newFile]);
  }, []);

  const handleOnDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!active || !over) return;
    if (active.id !== over.id) {
      setFilesToBeSaved((prevItems) => {
        const oldIndex = prevItems.findIndex((i) => active.id === i.id);
        const newIndex = prevItems.findIndex((i) => over.id === i.id);
        return arrayMove(prevItems, oldIndex, newIndex);
      });
    }
  }, []);

  useEffect(() => {
    const notificationMessage =
      notificationType === 'error'
        ? NOTIFICATION_MESSAGE[notificationType]
        : NOTIFICATION_MESSAGE[notificationType](getRemainingSpace(filesToBeSaved?.length || 0));

    handleSetMessage(notificationMessage.title, notificationMessage.subtitle, 'Close');
  }, [notificationType, filesToBeSaved?.length]);

  return {
    handleRemoveImages,
    handleOnDropImage,
    uploadedFiles,
    filesToBeSaved,
    handleFilesToBeSaved,
    handleOnDragEnd,
    NotificationModal: MemoizeNotificationModal,
    isOpen,
  };
};
